<script setup lang="ts">
import { computed } from 'vue';
import { RouterView, RouterLink } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useTenantStore } from '@sera/core';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import AppFooter from '../components/AppFooter.vue';

// `wide` (default false) keeps the existing 440px login/password-reset card
// width. The magic-link route opts in to a 720px card so the appointment
// content + tracking placeholder + share row from sketch 022 fit comfortably.
const props = defineProps<{ wide?: boolean }>();

const tenantStore = useTenantStore();
const tenantPhone = computed(() => tenantStore.config?.tenantPhone ?? '');
const logoUrl = computed(() => tenantStore.config?.logoUrl ?? '');
const cardMaxWidth = computed(() =>
  props.wide ? 'max-w-[720px]' : 'max-w-[440px]',
);
</script>

<template>
  <div class="h-dvh bg-canvas flex flex-col">
    <!-- D-05 / UI-SPEC PublicShell: branded strip -->
    <header
      data-testid="public-shell-strip"
      class="bg-secondary h-(--nav-height) w-full flex items-center px-4 sm:px-6"
    >
      <div class="flex items-center justify-between w-full">
        <RouterLink v-if="logoUrl" to="/" class="flex items-center">
          <img :src="logoUrl" alt="Logo" class="h-8 w-auto" />
        </RouterLink>
        <div v-if="tenantPhone" class="flex flex-col items-end leading-tight">
          <span class="text-white/80 text-xs">Need assistance?</span>
          <a
            :href="`tel:${tenantPhone}`"
            class="flex items-center gap-2 text-white text-sm hover:underline"
          >
            <FontAwesomeIcon :icon="faPhone" class="size-4" />
            <span>{{ tenantPhone }}</span>
          </a>
        </div>
      </div>
    </header>

    <!-- Centered card on light-grey body.
         main owns the scroll (main.css pins html/body to height:100%
         overflow:hidden). The inner min-h-full wrapper is what lets
         `m-auto` center a short card while still allowing a tall card
         (wide magic-link variant) to scroll from the top: when card
         content exceeds the viewport the wrapper grows past min-h-full,
         auto margins collapse to zero, and the card aligns to top
         inside the scroll container. Centering m-auto directly on a
         flex item with overflow-y-auto would clip the top of an
         overflowing item, breaking scroll-to-top. -->
    <main class="flex-1 min-h-0 overflow-y-auto">
      <div class="min-h-full flex flex-col px-4 py-8">
        <div
          data-testid="public-shell-card"
          :class="`bg-surface rounded-lg shadow-md ${cardMaxWidth} w-full m-auto p-8`"
        >
          <RouterView v-slot="{ Component }">
            <Transition name="page-fade" mode="out-in">
              <component :is="Component" />
            </Transition>
          </RouterView>
        </div>
      </div>
    </main>
    <AppFooter />
  </div>
</template>
